import * as THREE from 'three';
import { getPortalOutlinePoint, getPortalVisualKind } from './portalProfiles';

const TAU = Math.PI * 2;

// The frame and aperture use the same contour, including the fractured rift.
export const createPortalFrame = (route) => {
  const kind = getPortalVisualKind(route);
  const group = new THREE.Group();
  const metal = new THREE.MeshPhysicalMaterial({
    color: kind === 'Treasure' ? '#bba47a' : '#6a7f8e',
    emissive: route.color || '#79e9ff',
    emissiveIntensity: 0.035,
    metalness: 0.82,
    roughness: 0.24,
    clearcoat: 1,
    clearcoatRoughness: 0.18,
  });
  const inlay = new THREE.MeshStandardMaterial({
    color: '#0a111e',
    metalness: 0.8,
    roughness: 0.28,
  });
  const glow = new THREE.MeshBasicMaterial({
    color: route.color || '#79e9ff',
    transparent: true,
    opacity: route.locked ? 0.25 : 0.85,
  });
  const motions = [];
  const mesh = (geometry, material, parent = group) => {
    const object = new THREE.Mesh(geometry, material);
    parent.add(object);
    return object;
  };
  const arc = (
    rx,
    ry,
    thickness,
    material,
    start = 0,
    sweep = TAU,
    z = 0.02,
    parent = group,
    outlineKind = kind
  ) => {
    const steps = Math.max(24, Math.ceil(sweep * 24));
    const closed = sweep === TAU;
    const points = Array.from(
      { length: closed ? steps : steps + 1 },
      (_, index) => {
        const point = getPortalOutlinePoint(
          outlineKind,
          start + (index / steps) * sweep,
          rx,
          ry
        );
        return new THREE.Vector3(point.x, point.y, z);
      }
    );
    return mesh(
      new THREE.TubeGeometry(
        new THREE.CatmullRomCurve3(points, closed),
        steps,
        thickness,
        10,
        closed
      ),
      material,
      parent
    );
  };
  const jewel = (x, y, size = 0.1, parent = group) => {
    const object = mesh(new THREE.OctahedronGeometry(size), glow, parent);
    object.position.set(x, y, 0.15);
    return object;
  };
  const petal = (side, height, spread, material) => {
    const curve = new THREE.CubicBezierCurve3(
      new THREE.Vector3(0, -height, 0),
      new THREE.Vector3(side * spread, -height * 0.6, 0.06),
      new THREE.Vector3(side * spread, height * 0.7, 0.06),
      new THREE.Vector3(0, height, 0)
    );
    return mesh(new THREE.TubeGeometry(curve, 60, 0.045, 8, false), material);
  };

  if (kind !== 'Anomaly') {
    arc(1.25, 1.85, 0.095, inlay);
    arc(1.23, 1.83, 0.022, glow, 0, TAU, 0.13);
  }

  switch (kind) {
    case 'Warden': {
      group.name = 'warden-siege-frame';
      metal.color.set('#60323a');
      const siege = new THREE.Group();
      group.add(siege);
      for (let index = 0; index < 8; index++) {
        const angle = (index * TAU) / 8;
        arc(1.5, 2.12, 0.12, metal, angle + 0.07, TAU / 8 - 0.14, 0.06, siege);
        arc(1.4, 2.02, 0.032, glow, angle + 0.12, TAU / 8 - 0.24, 0.18);
        const point = getPortalOutlinePoint('Warden', angle, 1.62, 2.28);
        const blade = jewel(point.x, point.y, 0.17);
        blade.scale.set(0.4, 2.2, 0.65);
        blade.rotation.z = angle - Math.PI / 2;
      }
      const braces = [-1, 1].map((side) => {
        const brace = mesh(new THREE.BoxGeometry(0.08, 2.3, 0.14), metal);
        brace.position.set(side * 0.5, 0, 0.23);
        brace.rotation.z = side * 0.18;
        return brace;
      });
      motions.push((time, charge) => {
        siege.rotation.z = Math.sin(time * 0.5) * 0.035;
        braces.forEach((brace, index) => {
          brace.position.x = (index ? 1 : -1) * (0.5 + charge * 0.75);
        });
      });
      break;
    }
    case 'Core': {
      metal.color.set('#30283c');
      metal.emissiveIntensity = 0.12;
      arc(1.36, 2.02, 0.12, metal);
      const silver = new THREE.MeshPhysicalMaterial({
        color: '#c6bfd5',
        metalness: 0.9,
        roughness: 0.2,
        clearcoat: 1,
      });
      const hot = new THREE.MeshBasicMaterial({
        color: '#ff719c',
        transparent: true,
        opacity: 0.9,
      });
      const orbit = new THREE.Group();
      group.add(orbit);
      for (let i = 0; i < 6; i += 1) {
        const angle = (i * TAU) / 6;
        arc(
          1.62,
          2.25,
          0.055,
          silver,
          angle + 0.12,
          TAU / 6 - 0.28,
          0.1,
          orbit
        );
        arc(1.48, 2.13, 0.022, hot, angle + 0.19, TAU / 6 - 0.4, 0.16);
        const point = getPortalOutlinePoint(
          'Core',
          angle + Math.PI / 6,
          1.62,
          2.3
        );
        const shard = mesh(new THREE.OctahedronGeometry(0.23), silver);
        shard.position.set(point.x, point.y, 0.16);
        shard.scale.set(0.45, 1.9, 0.6);
        shard.rotation.z = angle + Math.PI / 6 - Math.PI / 2;
        const jewelPoint = getPortalOutlinePoint('Core', angle, 1.36, 2.02);
        jewel(jewelPoint.x, jewelPoint.y, 0.095);
      }
      motions.push((time, charge) => {
        orbit.rotation.z = Math.sin(time * 0.23) * 0.045;
        orbit.position.z = 0.05 + Math.sin(time * 0.5) * 0.06;
        hot.opacity = 0.6 + (Math.sin(time * 1.4) + 1) * 0.12 + charge * 0.15;
      });
      break;
    }
    case 'Supply': {
      arc(1.4, 1.98, 0.095, metal);
      arc(1.48, 2.06, 0.025, glow, 0.18, Math.PI - 0.36);
      arc(1.48, 2.06, 0.025, glow, Math.PI + 0.18, Math.PI - 0.36);
      [-1, 1].forEach((side) => {
        arc(1.65, 1.64, 0.065, metal, side > 0 ? -0.75 : Math.PI - 0.75, 1.5);
        for (let i = -1; i <= 1; i += 1) {
          const lens = mesh(new THREE.SphereGeometry(0.07, 12, 8), glow);
          lens.position.set(side * (1.63 - Math.abs(i) * 0.1), i * 0.31, 0.12);
        }
        const rail = new THREE.CubicBezierCurve3(
          new THREE.Vector3(side * 1.4, -1.65, -0.05),
          new THREE.Vector3(side * 1.76, -1.15, 0.18),
          new THREE.Vector3(side * 1.76, 1.15, 0.18),
          new THREE.Vector3(side * 1.4, 1.65, -0.05)
        );
        mesh(new THREE.TubeGeometry(rail, 50, 0.035, 8, false), glow);
      });
      break;
    }
    case 'Treasure': {
      arc(1.37, 1.99, 0.11, metal);
      arc(1.52, 2.08, 0.045, metal, 0.2, Math.PI - 0.4);
      arc(1.52, 2.08, 0.045, metal, Math.PI + 0.2, Math.PI - 0.4);
      [-1, 1].forEach((side) => {
        const crown = mesh(new THREE.OctahedronGeometry(0.23), metal);
        crown.scale.set(0.7, 1.35, 0.45);
        crown.position.set(0, side * 2.1, 0.02);
        jewel(0, side * 2.1, 0.11);
        jewel(side * 1.22, 0, 0.12);
      });
      for (let i = 0; i < 6; i += 1) {
        const point = getPortalOutlinePoint(
          kind,
          (i * TAU) / 6 + Math.PI / 6,
          1.4,
          2.02
        );
        const clasp = jewel(point.x, point.y, 0.09);
        clasp.scale.set(1, 1.5, 0.5);
      }
      break;
    }
    case 'Recovery': {
      arc(1.36, 1.96, 0.09, metal);
      [-1, 1].forEach((side) => {
        petal(side, 2.13, 2.02, metal);
        petal(side, 2.02, 1.89, glow);
      });
      const heartbeat = new THREE.CatmullRomCurve3(
        [
          [-0.32, 2.08, 0.1],
          [-0.14, 2.08, 0.1],
          [-0.04, 2.24, 0.1],
          [0.08, 1.99, 0.1],
          [0.17, 2.08, 0.1],
          [0.32, 2.08, 0.1],
        ].map((point) => new THREE.Vector3(...point)),
        false,
        'centripetal'
      );
      mesh(new THREE.TubeGeometry(heartbeat, 32, 0.025, 6, false), glow);
      [-1, 1].forEach((side) => {
        const reservoir = mesh(
          new THREE.CapsuleGeometry(0.065, 0.65, 4, 12),
          metal
        );
        reservoir.position.set(side * 1.51, 0, 0.06);
        const fluid = mesh(new THREE.CapsuleGeometry(0.032, 0.55, 4, 10), glow);
        fluid.position.set(side * 1.51, 0, 0.13);
        motions.push((time) => {
          fluid.scale.y = 0.8 + Math.sin(time * 1.3) * 0.15;
        });
      });
      break;
    }
    case 'Sanctuary': {
      arc(1.36, 1.98, 0.065, metal);
      [-1, 1].forEach((side) => {
        petal(side, 2.18, 2.1, metal);
        petal(side, 2.04, 2.27, glow);
      });
      const halo = arc(0.36, 0.14, 0.028, glow, 0, TAU, 0.02, group, null);
      halo.position.y = 2.2;
      jewel(0, -2.1, 0.1);
      for (let i = 0; i < 7; i += 1) {
        const mote = mesh(new THREE.SphereGeometry(0.027, 8, 6), glow);
        motions.push((time) => {
          const angle = (i * TAU) / 7 + time * 0.1;
          mote.position.set(
            Math.cos(angle) * 1.38,
            Math.sin(angle) * 1.9,
            Math.sin(angle * 2 + time * 0.2) * 0.3 + 0.14
          );
        });
      }
      break;
    }
    case 'Trial': {
      arc(1.39, 2.05, 0.085, metal);
      // Bevelled swept blades follow the arch instead of protruding cuboids.
      for (let i = 0; i < 4; i += 1) {
        const blade = new THREE.Shape();
        blade.moveTo(1.25, -0.2);
        blade.quadraticCurveTo(1.9, 0.22, 1.3, 1.25);
        blade.quadraticCurveTo(1.57, 0.38, 1.25, -0.2);
        const fin = mesh(
          new THREE.ExtrudeGeometry(blade, {
            depth: 0.1,
            bevelEnabled: true,
            bevelSegments: 3,
            steps: 1,
            bevelSize: 0.035,
            bevelThickness: 0.035,
          }),
          metal
        );
        fin.rotation.z = (i / 4) * TAU;
        fin.scale.y = 1.25;
        arc(1.45, 2.06, 0.027, glow, (i * Math.PI) / 2 + 0.15, 0.8);
      }
      [-1, 1].forEach((side) => {
        const spear = jewel(0, side * 2.16, 0.17);
        spear.scale.set(0.45, 1.5, 0.4);
      });
      break;
    }
    case 'Anomaly': {
      const obsidian = new THREE.MeshPhysicalMaterial({
        color: '#21162f',
        emissive: '#6c29c4',
        emissiveIntensity: 0.12,
        metalness: 0.86,
        roughness: 0.2,
        clearcoat: 1,
      });
      const cyan = new THREE.MeshBasicMaterial({
        color: '#56eaff',
        transparent: true,
        opacity: 0.85,
      });
      const magenta = new THREE.MeshBasicMaterial({
        color: '#f077df',
        transparent: true,
        opacity: 0.8,
      });
      const spectral = [cyan, magenta];
      // Long bevelled fragments follow the tear; the gaps open into space.
      for (let i = 0; i < 6; i += 1) {
        const start = (i * TAU) / 6 + 0.07;
        const sweep = TAU / 6 - (i % 2 ? 0.25 : 0.15);
        const piece = new THREE.Group();
        const shape = new THREE.Shape();
        for (let step = 0; step <= 24; step += 1) {
          const point = getPortalOutlinePoint(
            'Anomaly',
            start + (step / 24) * sweep,
            1.43,
            2.03
          );
          if (step === 0) shape.moveTo(point.x, point.y);
          else shape.lineTo(point.x, point.y);
        }
        for (let step = 24; step >= 0; step -= 1) {
          const point = getPortalOutlinePoint(
            'Anomaly',
            start + (step / 24) * sweep,
            1.3,
            1.9
          );
          shape.lineTo(point.x, point.y);
        }
        shape.closePath();
        mesh(
          new THREE.ExtrudeGeometry(shape, {
            depth: 0.12,
            steps: 1,
            bevelEnabled: true,
            bevelSize: 0.025,
            bevelThickness: 0.025,
            bevelSegments: 3,
          }),
          obsidian,
          piece
        );
        arc(1.29, 1.89, 0.024, spectral[i % 2], start, sweep, 0.18, piece);
        group.add(piece);
        motions.push((time, charge) => {
          const drift = Math.sin(time * 0.5 + i * 1.7) * 0.035;
          const displacement =
            drift + (route.locked ? Math.sin(time * 1.8 + i) * 0.06 : 0);
          piece.position.set(
            Math.cos(start) * displacement,
            Math.sin(start) * displacement,
            Math.sin(time * 0.45 + i * 2) * 0.12 + charge * 0.08
          );
          piece.rotation.z = Math.sin(time * 0.35 + i) * 0.015;
        });
      }
      // Two interwoven, non-planar streams pull around the broken containment.
      for (let strand = 0; strand < 2; strand += 1) {
        const points = [];
        for (let i = 0; i < 128; i += 1) {
          const angle = (i / 128) * TAU;
          const point = getPortalOutlinePoint('Anomaly', angle, 1.65, 2.09);
          const braid = Math.sin(angle * 3 + strand * Math.PI);
          points.push(
            new THREE.Vector3(
              point.x * (1 + braid * 0.07),
              point.y,
              Math.cos(angle * 3 + strand * Math.PI) * 0.36
            )
          );
        }
        const path = new THREE.CatmullRomCurve3(points, true);
        const stream = mesh(
          new THREE.TubeGeometry(path, 160, 0.016, 6, true),
          spectral[strand]
        );
        const comet = mesh(
          new THREE.SphereGeometry(0.055, 12, 8),
          spectral[strand]
        );
        motions.push((time) => {
          stream.rotation.y = Math.sin(time * 0.25 + strand * Math.PI) * 0.12;
          comet.position.copy(
            path.getPointAt((time * 0.065 + strand * 0.5) % 1)
          );
          comet.position.applyEuler(stream.rotation);
        });
      }
      const shardGeometry = new THREE.OctahedronGeometry(0.1);
      for (let i = 0; i < 12; i += 1) {
        const shard = mesh(
          shardGeometry,
          i % 3 === 0 ? spectral[i % 2] : obsidian
        );
        shard.scale.set(0.5, 1.7 + (i % 3) * 0.3, 0.6);
        motions.push((time) => {
          const angle = (i * TAU) / 12 + time * (i % 2 ? -0.035 : 0.045);
          const point = getPortalOutlinePoint(
            'Anomaly',
            angle,
            1.75 + Math.sin(i * 3) * 0.12,
            2.04
          );
          shard.position.set(
            point.x,
            point.y,
            Math.sin(angle * 2 + time * 0.3) * 0.55
          );
          shard.rotation.set(time * 0.25 + i, angle, -angle + time * 0.15);
        });
      }
      motions.push((time, charge) => {
        cyan.opacity =
          (route.locked ? 0.45 : 0.78) +
          Math.sin(time * 1.6) * 0.12 +
          charge * 0.1;
        magenta.opacity =
          (route.locked ? 0.4 : 0.7) +
          Math.cos(time * 1.4) * 0.12 +
          charge * 0.1;
      });
      break;
    }
    case 'Bonus Relic': {
      arc(1.38, 1.97, 0.08, metal);
      [-1, 1].forEach((side) => {
        arc(1.57, 2.1, 0.04, glow, side > 0 ? -0.9 : Math.PI - 0.9, 1.8);
        const crystal = jewel(0, side * 2.13, 0.23);
        crystal.scale.set(0.6, 1.5, 0.6);
      });
      for (let i = 0; i < 4; i += 1) {
        const crystal = jewel(i % 2 ? 1.28 : -1.28, i < 2 ? 0.65 : -0.65, 0.11);
        crystal.scale.set(0.6, 1.8, 0.6);
        motions.push((time) => {
          crystal.rotation.y = time * 0.3 + i;
        });
      }
      break;
    }
    case 'Salvage': {
      for (let i = 0; i < 3; i += 1) {
        const start = (i * TAU) / 3 + 0.1;
        arc(1.4, 1.99, 0.1, metal, start, TAU / 3 - 0.22);
        arc(1.5, 2.07, 0.023, glow, start + 0.06, TAU / 3 - 0.34);
        jewel(Math.cos(start) * 1.4, Math.sin(start) * 1.99, 0.065);
      }
      break;
    }
    default:
      arc(1.38, 2, 0.09, metal);
      arc(1.5, 2.12, 0.025, glow, 0.1, Math.PI - 0.2);
      arc(1.5, 2.12, 0.025, glow, Math.PI + 0.1, Math.PI - 0.2);
  }

  // A slim landing halo gives the structure weight without a polygonal plinth.
  const footing = arc(1.5, 0.72, 0.014, glow);
  footing.rotation.x = -Math.PI / 2;
  footing.position.y = -2.12;

  return {
    group,
    update(time, selected, charge = 0) {
      metal.emissiveIntensity = route.locked
        ? 0.015
        : 0.035 + (selected ? 0.09 : 0) + charge * 0.25;
      glow.opacity = route.locked
        ? 0.22 + Math.sin(time * 9) * 0.09
        : 0.75 + (selected ? 0.2 : 0);
      group.position.x = route.locked
        ? Math.sin(time * 13) * Math.sin(time * 3) * 0.028
        : 0;
      motions.forEach((animate) => animate(time, charge));
    },
  };
};
